import React, { useState } from "react";
import { Plus, Edit, Trash2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { useToast } from "../../../components/ui/toast";
import { useBenchmarksStore } from "../store";
import { PayrollBenchmark } from "../types";

const countries = ["PT", "ES", "IT", "FR", "GR", "HR", "DE", "NL", "GB"];
const roles = [
  "Receptionist",
  "Front Office Manager",
  "Housekeeper",
  "Housekeeping Supervisor",
  "Waiter",
  "Bartender",
  "Chef de Partie",
  "Sous Chef",
  "Executive Chef",
  "Spa Therapist",
  "Maintenance Technician",
  "Hotel Manager",
  "Assistant Manager",
  "Sales Manager",
  "Marketing Coordinator",
];

export default function PayrollTable() {
  const { toast } = useToast();
  const currentSetId = useBenchmarksStore((s) => s.currentSetId);
  const payroll = useBenchmarksStore((s) => s.payroll);
  const upsertPayroll = useBenchmarksStore((s) => s.upsertPayroll);
  const deletePayroll = useBenchmarksStore((s) => s.deletePayroll);

  const [editingItem, setEditingItem] = useState<PayrollBenchmark | null>(null);
  const [showDialog, setShowDialog] = useState(false);

  const handleAdd = () => {
    if (!currentSetId) return;

    const newItem: PayrollBenchmark = {
      id: crypto.randomUUID(),
      setId: currentSetId,
      country: "PT",
      role: "Receptionist",
      seniority: "mid",
      monthlyGrossLow: null,
      monthlyGrossMed: null,
      monthlyGrossHigh: null,
      currency: "EUR",
      includesBenefits: false,
    };

    setEditingItem(newItem);
    setShowDialog(true);
  };

  const handleEdit = (item: PayrollBenchmark) => {
    setEditingItem({ ...item });
    setShowDialog(true);
  };

  const handleSave = () => {
    if (!editingItem) return;

    if (!editingItem.country.trim() || !editingItem.role.trim()) {
      toast.error("Country and role are required");
      return;
    }

    upsertPayroll(editingItem);
    setShowDialog(false);
    setEditingItem(null);
    toast.success("Payroll benchmark saved");
  };

  const handleDelete = (item: PayrollBenchmark) => {
    if (
      !confirm(
        `Delete "${item.role} (${item.country})"? This cannot be undone.`
      )
    )
      return;

    deletePayroll(item.id);
    toast.success("Payroll benchmark deleted");
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value == null) return "—";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">
            Payroll Benchmarks
          </h3>
          <Button
            onClick={handleAdd}
            size="sm"
            className="flex items-center space-x-2 bg-primary-blue"
          >
            <Plus className="h-4 w-4" />
            <span>Add Role</span>
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-slate-700">
                  Country
                </th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">
                  Role
                </th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">
                  Seniority
                </th>
                <th className="text-right py-3 px-4 font-medium text-slate-700">
                  Low
                </th>
                <th className="text-right py-3 px-4 font-medium text-slate-700">
                  Medium
                </th>
                <th className="text-right py-3 px-4 font-medium text-slate-700">
                  High
                </th>
                <th className="text-center py-3 px-4 font-medium text-slate-700">
                  Inc. Benefits
                </th>
                <th className="text-center py-3 px-4 font-medium text-slate-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {payroll.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="py-3 px-4 font-medium text-slate-900">
                    {item.country}
                  </td>
                  <td className="py-3 px-4 text-slate-900">{item.role}</td>
                  <td className="py-3 px-4 text-slate-600 capitalize">
                    {item.seniority || "—"}
                  </td>
                  <td className="py-3 px-4 text-right text-slate-900">
                    {formatCurrency(item.monthlyGrossLow)}
                  </td>
                  <td className="py-3 px-4 text-right text-slate-900">
                    {formatCurrency(item.monthlyGrossMed)}
                  </td>
                  <td className="py-3 px-4 text-right text-slate-900">
                    {formatCurrency(item.monthlyGrossHigh)}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {item.includesBenefits ? "✓" : "—"}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(item)}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {payroll.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            <p>
              No payroll benchmarks yet. Add your first role to get started.
            </p>
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl p-6">
          <DialogHeader className="pb-4 border-b border-slate-100 mb-4">
            <DialogTitle className="text-2xl font-bold text-slate-900">
              {editingItem?.role
                ? "Edit Payroll Benchmark"
                : "Add Payroll Benchmark"}
            </DialogTitle>
          </DialogHeader>

          {editingItem && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={editingItem.country}
                    onValueChange={(value) =>
                      setEditingItem({ ...editingItem, country: value })
                    }
                  >
                    <SelectTrigger className="focus:ring-blue-500 focus:border-blue-500">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={editingItem.role}
                    onValueChange={(value) =>
                      setEditingItem({ ...editingItem, role: value })
                    }
                  >
                    <SelectTrigger className="focus:ring-blue-500 focus:border-blue-500">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Seniority
                  </label>
                  <Select
                    value={editingItem.seniority || ""}
                    onValueChange={(value) =>
                      setEditingItem({
                        ...editingItem,
                        seniority: value as any,
                      })
                    }
                  >
                    <SelectTrigger className="focus:ring-blue-500 focus:border-blue-500">
                      <SelectValue placeholder="Select seniority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="junior">Junior</SelectItem>
                      <SelectItem value="mid">Mid</SelectItem>
                      <SelectItem value="senior">Senior</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Monthly Gross (Low)
                  </label>
                  <Input
                    type="number"
                    value={editingItem.monthlyGrossLow || ""}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        monthlyGrossLow: Number(e.target.value) || null,
                      })
                    }
                    placeholder="0"
                    min="0"
                    className="focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Monthly Gross (Medium)
                  </label>
                  <Input
                    type="number"
                    value={editingItem.monthlyGrossMed || ""}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        monthlyGrossMed: Number(e.target.value) || null,
                      })
                    }
                    placeholder="0"
                    min="0"
                    className="focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Monthly Gross (High)
                  </label>
                  <Input
                    type="number"
                    value={editingItem.monthlyGrossHigh || ""}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        monthlyGrossHigh: Number(e.target.value) || null,
                      })
                    }
                    placeholder="0"
                    min="0"
                    className="focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Currency
                  </label>
                  <Select
                    value={editingItem.currency}
                    onValueChange={(value) =>
                      setEditingItem({ ...editingItem, currency: value })
                    }
                  >
                    <SelectTrigger className="focus:ring-blue-500 focus:border-blue-500">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-1">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-6 bg-slate-200 rounded-full flex items-center p-1 cursor-pointer">
                      <div
                        className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${editingItem.includesBenefits ? "translate-x-4 bg-blue-600" : "translate-x-0"}`}
                      ></div>
                    </div>
                    <label className="text-sm font-medium text-slate-700 select-none">
                      Includes Benefits
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="pt-4 border-t border-slate-100 mt-6">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Benchmark</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
